import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Phone, Mail, MapPin, Clock, CheckCircle, Loader2, Send } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const insuranceTypes = [
  "Home Insurance", "Auto Insurance", "Commercial Insurance", "Life Insurance",
  "Health Insurance", "Annuities", "LegalShield", "Long-term Care", "Travel Insurance", "Other"
];

export default function ContactPage() {
  const [formType, setFormType] = useState("quote");
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", insurance_type: "", message: "", subject: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (formType === "quote") {
        await axios.post(`${API}/leads`, {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          insurance_type: formData.insurance_type,
          message: formData.message,
        });
      } else {
        await axios.post(`${API}/contact`, {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          subject: formData.subject,
          message: formData.message,
        });
      }
      setSuccess(true);
      setFormData({ name: "", email: "", phone: "", insurance_type: "", message: "", subject: "" });
    } catch (e) {
      console.error("Submit failed", e);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      {/* Hero */}
      <section className="hero-gradient py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-4 animate-fade-up">
            {formType === "quote" ? "Get a Free Quote" : "Contact Us"}
          </h1>
          <p className="text-white/70 text-base lg:text-lg max-w-2xl mx-auto animate-fade-up delay-100">
            Reach out to our team for personalized insurance solutions. We're here to help.
          </p>
        </div>
      </section>

      <section className="bg-[#F8FAFC] py-16 sm:py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Form */}
            <div className="lg:col-span-2">
              {/* Toggle */}
              <div className="flex gap-2 mb-8">
                <Button
                  data-testid="tab-quote"
                  onClick={() => { setFormType("quote"); setSuccess(false); }}
                  className={formType === "quote"
                    ? "bg-[#D4AF37] text-[#002147] hover:bg-[#B5952F] font-bold"
                    : "bg-white text-slate-500 border border-slate-200 hover:bg-slate-50"}
                >
                  Get a Quote
                </Button>
                <Button
                  data-testid="tab-contact"
                  onClick={() => { setFormType("contact"); setSuccess(false); }}
                  className={formType === "contact"
                    ? "bg-[#D4AF37] text-[#002147] hover:bg-[#B5952F] font-bold"
                    : "bg-white text-slate-500 border border-slate-200 hover:bg-slate-50"}
                >
                  General Inquiry
                </Button>
              </div>

              {success ? (
                <Card data-testid="form-success" className="bg-white rounded-xl">
                  <CardContent className="p-12 text-center">
                    <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-6">
                      <CheckCircle className="h-8 w-8 text-emerald-500" />
                    </div>
                    <h3 className="font-heading text-2xl font-bold text-[#002147] mb-3">Thank You!</h3>
                    <p className="text-slate-500 mb-6">
                      {formType === "quote"
                        ? "We've received your quote request. Our team will get back to you within 24 hours."
                        : "Your message has been sent. We'll respond as soon as possible."}
                    </p>
                    <Button
                      data-testid="submit-another-btn"
                      onClick={() => setSuccess(false)}
                      className="bg-[#D4AF37] text-[#002147] hover:bg-[#B5952F] font-bold"
                    >
                      Submit Another Request
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <Card className="bg-white rounded-xl">
                  <CardContent className="p-8">
                    <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-6">
                      <div className="grid sm:grid-cols-2 gap-6">
                        <div>
                          <label className="text-sm font-semibold text-[#002147] uppercase tracking-wide mb-2 block">Full Name *</label>
                          <Input
                            data-testid="input-name"
                            required
                            value={formData.name}
                            onChange={(e) => handleChange("name", e.target.value)}
                            placeholder="John Doe"
                            className="h-12 border-slate-300 focus:border-[#002147] focus:ring-1 focus:ring-[#002147]"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-semibold text-[#002147] uppercase tracking-wide mb-2 block">Email *</label>
                          <Input
                            data-testid="input-email"
                            type="email"
                            required
                            value={formData.email}
                            onChange={(e) => handleChange("email", e.target.value)}
                            placeholder="john@example.com"
                            className="h-12 border-slate-300 focus:border-[#002147] focus:ring-1 focus:ring-[#002147]"
                          />
                        </div>
                      </div>

                      <div className="grid sm:grid-cols-2 gap-6">
                        <div>
                          <label className="text-sm font-semibold text-[#002147] uppercase tracking-wide mb-2 block">Phone</label>
                          <Input
                            data-testid="input-phone"
                            value={formData.phone}
                            onChange={(e) => handleChange("phone", e.target.value)}
                            placeholder="(219) 555-0123"
                            className="h-12 border-slate-300 focus:border-[#002147] focus:ring-1 focus:ring-[#002147]"
                          />
                        </div>
                        <div>
                          {formType === "quote" ? (
                            <>
                              <label className="text-sm font-semibold text-[#002147] uppercase tracking-wide mb-2 block">Insurance Type</label>
                              <Select value={formData.insurance_type} onValueChange={(v) => handleChange("insurance_type", v)}>
                                <SelectTrigger data-testid="select-insurance-type" className="h-12 border-slate-300">
                                  <SelectValue placeholder="Select type" />
                                </SelectTrigger>
                                <SelectContent>
                                  {insuranceTypes.map((t) => (
                                    <SelectItem key={t} value={t}>{t}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </>
                          ) : (
                            <>
                              <label className="text-sm font-semibold text-[#002147] uppercase tracking-wide mb-2 block">Subject</label>
                              <Input
                                data-testid="input-subject"
                                value={formData.subject}
                                onChange={(e) => handleChange("subject", e.target.value)}
                                placeholder="How can we help?"
                                className="h-12 border-slate-300 focus:border-[#002147] focus:ring-1 focus:ring-[#002147]"
                              />
                            </>
                          )}
                        </div>
                      </div>

                      <div>
                        <label className="text-sm font-semibold text-[#002147] uppercase tracking-wide mb-2 block">Message</label>
                        <Textarea
                          data-testid="input-message"
                          value={formData.message}
                          onChange={(e) => handleChange("message", e.target.value)}
                          placeholder={formType === "quote" ? "Tell us about your insurance needs..." : "Your message..."}
                          className="min-h-[120px] border-slate-300 focus:border-[#002147] focus:ring-1 focus:ring-[#002147]"
                        />
                      </div>

                      <Button
                        data-testid="submit-form-btn"
                        type="submit"
                        disabled={submitting}
                        className="w-full sm:w-auto bg-[#D4AF37] text-[#002147] hover:bg-[#B5952F] font-bold h-12 px-8"
                      >
                        {submitting ? (
                          <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sending...</>
                        ) : (
                          <><Send className="h-4 w-4 mr-2" />{formType === "quote" ? "Request Quote" : "Send Message"}</>
                        )}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <Card className="bg-white rounded-xl">
                <CardContent className="p-6">
                  <h3 className="font-heading text-lg font-semibold text-[#002147] mb-4">Contact Information</h3>
                  <div className="space-y-4">
                    <a href="tel:2197797040" data-testid="contact-phone-link" className="flex items-start gap-3 text-slate-600 hover:text-[#002147] text-sm transition-colors">
                      <Phone className="h-5 w-5 text-[#D4AF37] shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-[#002147]">Phone</p>
                        <p>(219) 779-7040</p>
                      </div>
                    </a>
                    <a href="mailto:customerservice@a2insure.com" className="flex items-start gap-3 text-slate-600 hover:text-[#002147] text-sm transition-colors">
                      <Mail className="h-5 w-5 text-[#D4AF37] shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-[#002147]">Email</p>
                        <p>customerservice@a2insure.com</p>
                      </div>
                    </a>
                    <div className="flex items-start gap-3 text-slate-600 text-sm">
                      <MapPin className="h-5 w-5 text-[#D4AF37] shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-[#002147]">Address</p>
                        <p>10971 Four Seasons Pl, Ste 216<br />Crown Point, IN 46307</p>
<p>Elmhurst, IL<br />(312) 937-4444</p>
<p>Naples, FL<br />(239) 525-2712</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 text-slate-600 text-sm">
                      <MapPin className="h-5 w-5 text-[#D4AF37] shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-[#002147]">Elmhurst, IL</p>
                        <a href="tel:3129374444" className="hover:text-[#002147] transition-colors">(312) 937-4444</a>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 text-slate-600 text-sm">
                      <MapPin className="h-5 w-5 text-[#D4AF37] shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-[#002147]">Naples, FL</p>
                        <a href="tel:2395252712" className="hover:text-[#002147] transition-colors">(239) 525-2712</a>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 text-slate-600 text-sm">
                      <Clock className="h-5 w-5 text-[#D4AF37] shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-[#002147]">Hours</p>
                        <p>Mon - Fri: 9:00 AM - 5:00 PM<br />Sat - Sun: Closed</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-[#002147] rounded-xl">
                <CardContent className="p-6 text-center">
                  <Phone className="h-8 w-8 text-[#D4AF37] mx-auto mb-3" />
                  <h4 className="text-white font-heading text-lg font-semibold mb-2">Prefer to Talk?</h4>
                  <p className="text-white/60 text-sm mb-4">Our agents are ready to help you find the right coverage.</p>
                  <a href="tel:2197797040">
                    <Button data-testid="sidebar-call-btn" className="w-full bg-[#D4AF37] text-[#002147] hover:bg-[#B5952F] font-bold">
                      Call Now
                    </Button>
                  </a>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
